import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TabPanel, TabView } from "primereact/tabview";
import { Dialog } from "primereact/dialog";
import { PageProps } from "../../../models/routing.model";
import UiCard from "../../../components/card/Card";
import UiTable from "../../../components/table/Table.component";
import UiButton from "../../../components/button/Button";
import {
	TableColumn,
	TableColumnTemplate,
	TableDateMode,
} from "../../../models/components/table/table-column.model";
import { TablePagination } from "../../../models/components/table/table-pagination.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import { ParticipantContext } from "../../../contexts/Participant.context";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import { usePromptJobOutputs } from "../../../hooks/usePromptJobOutputs";
import { IPromptJobOutput } from "../../../models/entities/prompt-job-output.model";
import { TableTemplateButtonsEvent } from "../../../models/components/table/table-template.model";
import { formatInputMetadata, prettyInputMetadata } from "../../../utils/input-metadata";

interface PromptJobOutputRow {
	outputHash: string;
	model: string;
	inputMetadata: string | null;
	outputSnippet: string;
	createdOn: string;
	actionsTemplate: TableTemplateButtonsEvent[];
	_output: IPromptJobOutput;
}

const SNIPPET_LENGTH = 120;

const PromptJobOutputs = (_props: PageProps) => {
	const { t } = useTranslation("common");
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);
	const participantId = participantContext.participant?.id ?? 0;

	const outputsQuery = usePromptJobOutputs({ participantId });

	const [selectedOutput, setSelectedOutput] = useState<IPromptJobOutput | null>(null);
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "createdOn",
		sortOrder: -1,
		search: undefined,
		filters: undefined,
	});

	const promptNames: string[] = useMemo(() => {
		if (!outputsQuery.data) return [];
		const names = Array.from(new Set(outputsQuery.data.map((o) => o.promptName)));
		return names.sort();
	}, [outputsQuery.data]);

	const rowsByPrompt = useMemo<Record<string, PromptJobOutputRow[]>>(() => {
		if (!outputsQuery.data) return {};
		const grouped: Record<string, PromptJobOutputRow[]> = {};
		for (const output of outputsQuery.data) {
			if (!grouped[output.promptName]) grouped[output.promptName] = [];
			grouped[output.promptName].push({
				outputHash: output.outputHash,
				model: output.model,
				inputMetadata: formatInputMetadata(output.inputMetadata),
				outputSnippet:
					output.outputText.length > SNIPPET_LENGTH
						? output.outputText.slice(0, SNIPPET_LENGTH) + "…"
						: output.outputText,
				createdOn: output.createdOn,
				actionsTemplate: [],
				_output: output,
			});
		}
		return grouped;
	}, [outputsQuery.data]);

	const columns: TableColumn[] = [
		{
			field: "createdOn",
			title: "PARTICIPANT.PROMPT_JOB_OUTPUTS.TABLE.COLUMNS.createdOn",
			sortable: true,
			template: TableColumnTemplate.DATE,
			templateOptions: {
				dateMode: TableDateMode.UTC,
				dateFormat: {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
					hour: "2-digit",
					minute: "2-digit",
					hour12: true,
				},
			},
		},
		{
			field: "model",
			title: "PARTICIPANT.PROMPT_JOB_OUTPUTS.TABLE.COLUMNS.model",
			sortable: true,
		},
		{
			field: "inputMetadata",
			title: "PARTICIPANT.PROMPT_JOB_OUTPUTS.TABLE.COLUMNS.inputMetadata",
			sortable: false,
			style: {
				maxWidth: "200px",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			},
		},
		{
			field: "outputSnippet",
			title: "PARTICIPANT.PROMPT_JOB_OUTPUTS.TABLE.COLUMNS.output",
			sortable: false,
			style: {
				maxWidth: "350px",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
			},
		},
		{
			field: "actionsTemplate",
			template: TableColumnTemplate.CUSTOM,
			alignment: "right",
			style: { width: "120px", minWidth: "120px", flexShrink: 0 },
			templateOptions: {
				customTemplate: (_row) => {
					const row = _row as PromptJobOutputRow;
					return (
						<UiButton
							type="button"
							className="p-button-rounded p-button-sm p-button-outlined"
							label="PARTICIPANT.PROMPT_JOB_OUTPUTS.BUTTON_VIEW"
							onClick={() => setSelectedOutput(row._output)}
						/>
					);
				},
			},
		},
	];

	const messages = new TableMessages();
	const message = (promptName: string): UiFieldMessageProps => {
		const rows = rowsByPrompt[promptName] ?? [];
		return outputsQuery.isLoading
			? { severity: FieldMessageSeverity.LOADING, label: messages.loading }
			: outputsQuery.isError
				? { severity: FieldMessageSeverity.DANGER, label: messages.error }
				: rows.length === 0
					? { severity: FieldMessageSeverity.INFO, label: messages.empty }
					: { severity: FieldMessageSeverity.INFO, label: "" };
	};

	const onPagination = (event: TablePagination): void => {
		setPagination({
			...pagination,
			page: event.page,
			rows: event.rows,
			sortField: event.sortField,
			sortOrder: event.sortOrder,
			first: event.first,
		});
	};

	const formatPromptName = (name: string): string =>
		name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

	if (
		outputsQuery.isLoading ||
		outputsQuery.isError ||
		(outputsQuery.isSuccess && promptNames.length === 0)
	) {
		const loadingMessage: UiFieldMessageProps = outputsQuery.isLoading
			? { severity: FieldMessageSeverity.LOADING, label: messages.loading }
			: outputsQuery.isError
				? { severity: FieldMessageSeverity.DANGER, label: messages.error }
				: { severity: FieldMessageSeverity.INFO, label: messages.empty };

		return (
			<UiCard
				title="PARTICIPANT.PROMPT_JOB_OUTPUTS.TITLE"
				titleUnderline={false}
			>
				<UiTable
					dataKey="outputHash"
					useAsCard={false}
					columns={columns}
					value={[]}
					message={loadingMessage}
					isServerPaginated={false}
					pagination={pagination}
					paginationFn={onPagination}
					totalRecords={0}
				/>
			</UiCard>
		);
	}

	return (
		<>
			<UiCard
				title="PARTICIPANT.PROMPT_JOB_OUTPUTS.TITLE"
				titleUnderline={false}
			>
				{promptNames.length > 0 ? (
					<TabView>
						{promptNames.map((promptName) => (
							<TabPanel
								key={promptName}
								header={formatPromptName(promptName)}
							>
								<UiTable
									dataKey="outputHash"
									useAsCard={false}
									columns={columns}
									value={rowsByPrompt[promptName] ?? []}
									message={message(promptName)}
									isServerPaginated={false}
									pagination={pagination}
									paginationFn={onPagination}
									totalRecords={(rowsByPrompt[promptName] ?? []).length}
								/>
							</TabPanel>
						))}
					</TabView>
				) : (
					<UiTable
						dataKey="outputHash"
						useAsCard={false}
						columns={columns}
						value={[]}
						message={
							outputsQuery.isError
								? { severity: FieldMessageSeverity.DANGER, label: messages.error }
								: { severity: FieldMessageSeverity.INFO, label: messages.empty }
						}
						isServerPaginated={false}
						pagination={pagination}
						paginationFn={onPagination}
						totalRecords={0}
					/>
				)}
			</UiCard>

			<Dialog
				header={selectedOutput ? formatPromptName(selectedOutput.promptName) : ""}
				visible={!!selectedOutput}
				style={{ width: "60vw", maxWidth: "900px" }}
				onHide={() => setSelectedOutput(null)}
				dismissableMask
			>
				{selectedOutput && (
					<>
						<div
							style={{
								marginBottom: "0.75rem",
								color: "var(--text-color-secondary)",
								fontSize: "0.875rem",
							}}
						>
							<strong>{t("PARTICIPANT.PROMPT_JOB_OUTPUTS.DIALOG.MODEL")}:</strong>{" "}
							{selectedOutput.model}
							{selectedOutput.inputMetadata && (
								<>
									{" · "}
									<strong>
										{t("PARTICIPANT.PROMPT_JOB_OUTPUTS.DIALOG.INPUT")}:
									</strong>{" "}
									{formatInputMetadata(selectedOutput.inputMetadata)}
								</>
							)}
						</div>
						{selectedOutput.inputMetadata && (
							<div
								style={{
									marginBottom: "0.75rem",
									fontSize: "0.85rem",
									color: "var(--text-color-secondary)",
								}}
							>
								<details>
									<summary style={{ cursor: "pointer", marginBottom: "0.5rem" }}>
										{t("PARTICIPANT.PROMPT_JOB_OUTPUTS.DIALOG.RAW_INPUT")}
									</summary>
									<pre
										style={{
											whiteSpace: "pre-wrap",
											wordBreak: "break-word",
											fontFamily: "inherit",
											fontSize: "0.8rem",
											background: "var(--surface-ground)",
											padding: "0.75rem",
											borderRadius: "4px",
											maxHeight: "20vh",
											overflowY: "auto",
											margin: "0.5rem 0 0 0",
										}}
									>
										{prettyInputMetadata(selectedOutput.inputMetadata)}
									</pre>
								</details>
							</div>
						)}
						<pre
							style={{
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								fontFamily: "inherit",
								fontSize: "0.9rem",
								background: "var(--surface-ground)",
								padding: "1rem",
								borderRadius: "6px",
								maxHeight: "60vh",
								overflowY: "auto",
							}}
						>
							{selectedOutput.outputText}
						</pre>
					</>
				)}
			</Dialog>
		</>
	);
};

export default PromptJobOutputs;
